// src/components/modals/ScheduleExportModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Clock, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../../utils/api';

export default function ScheduleExportModal({ isOpen, onClose, chartId, chartTitle }) {
  const [frequency, setFrequency] = useState('daily');
  const [time, setTime] = useState('09:00');
  const [email, setEmail] = useState('');
  const [format, setFormat] = useState('png');
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadSchedules();
    }
  }, [isOpen]);

  async function loadSchedules() {
    setLoading(true);
    try {
      const res = await apiCall(`/charts/${chartId}/scheduled-exports`);
      if (res.ok) {
        const data = await res.json();
        setSchedules(data.scheduled_exports || []);
      }
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!email) {
      alert('Please enter an email address');
      return;
    }

    setSaving(true);
    try {
      const res = await apiCall(`/charts/${chartId}/scheduled-exports`, {
        method: 'POST',
        body: JSON.stringify({
          frequency,
          time: time + ':00',
          email,
          format,
        })
      });

      if (res.ok) {
        setEmail('');
        loadSchedules();
      } else {
        alert('Failed to schedule export');
      }
    } catch (err) {
      console.error('Failed to schedule export:', err);
      alert('Failed to schedule export');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(scheduleId) {
    if (!window.confirm('Delete this scheduled export?')) {
      return;
    }

    try {
      const res = await apiCall(`/charts/scheduled-exports/${scheduleId}`, {
        method: 'DELETE'
      });

      if (res.status === 204 || res.ok) {
        loadSchedules();
      } else {
        alert('Failed to delete schedule');
      }
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      alert('Failed to delete schedule');
    }
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#2a2a2a]">
            <div>
              <h2 className="text-xl font-semibold text-white">Schedule Export</h2>
              <p className="text-sm text-[#6a6a6a] mt-1">{chartTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[#a0a0a0]" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Existing Schedules */}
                {schedules.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-white">Active Schedules</h3>
                    {schedules.map(schedule => (
                      <div 
                        key={schedule.id}
                        className="p-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-white font-medium">
                              {schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)} at {schedule.time?.slice(0, 5)}
                            </p>
                            <p className="text-xs text-[#6a6a6a] mt-1">
                              {schedule.email} • {schedule.format.toUpperCase()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDelete(schedule.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Create New Schedule */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-white">
                    {schedules.length > 0 ? 'Create Another Schedule' : 'Create Schedule'}
                  </h3>

                  <div>
                    <label className="text-xs text-[#6a6a6a] block mb-2">Frequency</label>
                    <select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-[#6a6a6a] block mb-2">Time</label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-[#6a6a6a] block mb-2">Email Address</label>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 placeholder:text-[#4a4a4a]"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-[#6a6a6a] block mb-2">Export Format</label>
                    <select
                      value={format}
                      onChange={(e) => setFormat(e.target.value)}
                      className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="png">PNG Image</option>
                      <option value="pdf">PDF Document</option>
                      <option value="both">Both (PNG & PDF)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a2a]">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving || loading}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4" />
                  Schedule Export
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}